import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Heart } from "lucide-react";
import { toast } from "sonner";

export default function WishlistPage() {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) fetchWishlist();
    else setLoading(false);
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(data);
    } catch (error) {
      console.error("Failed to fetch wishlist");
      toast.error("Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
      setWishlist((prev) => prev.filter((w) => w.product_id !== productId));
      toast.success("Removed from wishlist");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="cormorant text-3xl font-light mb-4">Sign in to view wishlist</h2>
            <Link
              to="/login"
              className="inline-block bg-zinc-900 text-white px-8 py-3 uppercase tracking-widest text-xs"
            >
              Sign In
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 px-6 md:px-12 max-w-7xl mx-auto py-12">
        <h1
          className="cormorant text-4xl font-light tracking-tight mb-12"
          data-testid="wishlist-title"
        >
          My Wishlist
        </h1>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent" />
          </div>
        ) : wishlist.length === 0 ? (
          <div className="text-center py-12">
            <Heart size={48} className="mx-auto mb-4 text-zinc-200" />
            <p className="text-zinc-600 mb-6">Your wishlist is empty.</p>
            <Link
              to="/shop"
              className="inline-block border border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-8 py-4 uppercase tracking-widest text-xs transition-colors"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-500 mb-8">{wishlist.length} saved item{wishlist.length !== 1 ? "s" : ""}</p>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {wishlist.map((item) => (
                <div
                  key={item.product_id}
                  className="group relative"
                  data-testid={`wishlist-item-${item.product_id}`}
                >
                  {/* Remove button */}
                  <button
                    onClick={() => removeFromWishlist(item.product_id)}
                    className="absolute top-2 right-2 z-10 bg-white rounded-full p-1.5 shadow-md text-red-500 hover:text-red-600 hover:scale-110 transition-all"
                    data-testid={`remove-wishlist-${item.product_id}`}
                    aria-label="Remove from wishlist"
                  >
                    <Heart size={16} fill="currentColor" />
                  </button>

                  <Link to={`/product/${item.product_id}`} className="block">
                    <div className="relative overflow-hidden bg-zinc-100 mb-4 aspect-[4/5]">
                      <img
                        src={resolveImageUrl(item.product?.image_url)}
                        alt={item.product?.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {item.product?.stock === 0 && (
                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                          <span className="text-xs uppercase tracking-widest text-zinc-500 bg-white px-3 py-1 border border-zinc-200">
                            Out of Stock
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-light cormorant mb-0.5 group-hover:text-zinc-600 transition-colors">
                      {item.product?.name}
                    </h3>
                    <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">
                      {item.product?.category}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">₹{item.product?.price?.toLocaleString()}</p>
                      {item.product?.compare_at_price > item.product?.price && (
                        <p className="text-xs text-zinc-400 line-through">
                          ₹{item.product?.compare_at_price?.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
